import { create } from "zustand";
import { api, type DealListItem } from "@/lib/api";
import type { FacilityListItem } from "@/pages/facility/facility.types";
import type { BorrowerProfileInput, BorrowerProfileResponse } from "@/types/borrowerProfile";
import type { BorrowerFinancialsResponse, FinancialDocumentRecord } from "@/types/borrowerFinancials";

type KycStatus = "PENDING" | "APPROVED" | "REJECTED";

export type BorrowerRecord = {
  id: string;
  legal_name: string;
  country: string | null;
  jurisdiction: string | null;
  currency: string;
  kyc_status: KycStatus;
  facility_count: number;
  has_active_book: boolean;
  /** Present on every borrower payload. The bytes themselves only travel on the
   *  detail endpoint and on GET /borrowers/logos, never in list responses. */
  has_logo?: boolean;
  /** Same rule for the presentation card: the flag travels everywhere, the
   *  document only on the detail and profile endpoints. */
  has_profile?: boolean;
  /** HU-066 — the name anonymous guests see instead of `legal_name`. Always
   *  resolved by the server: with the column unset it derives one from the id,
   *  so this is never blank and is what a guest would actually be shown.
   *  `display_alias_set` says whether somebody chose it. */
  display_alias?: string;
  display_alias_set?: boolean;
};

export type BookRecord = {
  id: string;
  borrower_id: string;
  currency: string;
  /** Operator-declared, edited from the borrower's settings page. */
  asset_class: string | null;
  asset_class_label: string | null;
  /** This originator lends in USDT. Operator-set; a book can be denominated in
   *  another currency and still be funded through the USDT line. */
  usdt_lending: boolean;
  reported_debtors: number | null;
  reported_debtors_as_of: string | null;
  reported_debtors_source: string | null;
  /** Resolved count: the stated figure when there is one, the tape's otherwise,
   *  with a marker saying which. */
  debtors: {
    value: number | null;
    source: "reported" | "computed" | null;
    computed: number | null;
    reported: number | null;
    reported_as_of: string | null;
    reported_source: string | null;
    loans_per_debtor: number | null;
    computed_looks_per_loan: boolean;
  } | null;
  has_mappings: boolean;
  snapshot_date: string | null;
  facility_count: number;
  loan_count: number;
  payment_count: number;
  customer_count: number;
  collateral_count: number;
};

/** One row of `GET /facilities`, no more. It used to declare a `metrics` block
 *  that the list endpoint does not serve — only `/facilities/summary` does — and
 *  the cast below asserted it anyway, so the sidebar's covenant dot read
 *  `undefined` forever. */
export type BorrowerFacility = FacilityListItem;

/** The borrower's contracts. Loaded next to the facilities because the
 *  workspace list groups one under the other, and `GET /facilities` carries
 *  only the `deal_id`, never the contract's name. */
export type BorrowerDeal = DealListItem;

/** One entry per borrower whose card has been asked for. Keyed rather than
 *  singular because the card is read from three places — the borrower page, a
 *  book tab and a facility's Overview — and the last two are reached without ever
 *  loading the borrower record itself (those endpoints are admin-only). */
type ProfileEntry = {
  data: BorrowerProfileResponse | null;
  loading: boolean;
  error: string;
};

/** Same keyed shape as the profile, for the same reason: the Financials tab of
 *  a book is reached without ever loading the borrower record (that endpoint is
 *  admin-only). Documents are a separate entry because they are admin-only and
 *  a non-admin's read of the figures must not depend on a call that 403s. */
type FinancialsEntry = {
  data: BorrowerFinancialsResponse | null;
  loading: boolean;
  error: string;
};

type FinancialDocsEntry = {
  data: FinancialDocumentRecord[] | null;
  loading: boolean;
  error: string;
};

type BorrowerStore = {
  borrowerId: string | null;
  borrower: BorrowerRecord | null;
  books: BookRecord[];
  facilities: BorrowerFacility[];
  deals: BorrowerDeal[];
  loading: boolean;
  error: string;
  _loadPromise: Promise<void> | null;
  load: (borrowerId: string) => Promise<void>;
  invalidate: () => void;
  reset: () => void;

  profiles: Record<string, ProfileEntry>;
  loadProfile: (borrowerId: string) => Promise<void>;
  saveProfile: (borrowerId: string, profile: BorrowerProfileInput) => Promise<void>;

  financials: Record<string, FinancialsEntry>;
  financialDocs: Record<string, FinancialDocsEntry>;
  loadFinancials: (borrowerId: string, force?: boolean) => Promise<void>;
  loadFinancialDocs: (borrowerId: string, force?: boolean) => Promise<void>;
};

export const useBorrowerStore = create<BorrowerStore>((set, get) => ({
  borrowerId: null,
  borrower: null,
  books: [],
  facilities: [],
  deals: [],
  loading: false,
  error: "",
  _loadPromise: null,

  load: async (borrowerId) => {
    const s = get();
    if (s.borrowerId === borrowerId && s.borrower) return;
    if (s.borrowerId === borrowerId && s._loadPromise) return s._loadPromise;

    const promise = (async () => {
      set({ loading: true, error: "", borrowerId });
      try {
        const [borrower, books, allFacilities, allDeals] = await Promise.all([
          api.getBorrower(borrowerId) as Promise<BorrowerRecord>,
          api.listBorrowerBooks(borrowerId) as Promise<BookRecord[]>,
          api.listFacilities(),
          api.listDeals(),
        ]);
        set({
          borrower,
          books,
          facilities: allFacilities.filter((d) => d.borrower?.id === borrowerId),
          deals: allDeals.filter((d) => d.borrower_id === borrowerId),
        });
      } catch (e) {
        set({ error: String(e) });
      } finally {
        set({ loading: false, _loadPromise: null });
      }
    })();

    set({ _loadPromise: promise });
    return promise;
  },

  invalidate: () => set({ borrowerId: null, _loadPromise: null }),

  reset: () => set({
    borrowerId: null, borrower: null, books: [], facilities: [], deals: [],
    loading: false, error: "", _loadPromise: null,
    profiles: {}, financials: {}, financialDocs: {},
  }),

  profiles: {},

  loadProfile: async (borrowerId) => {
    const entry = get().profiles[borrowerId];
    // Already resolved or in flight — the API cache dedupes the request, but
    // re-entering would still flip `loading` back on under a rendered card.
    if (entry && (entry.data || entry.loading)) return;
    set((s) => ({ profiles: { ...s.profiles, [borrowerId]: { data: null, loading: true, error: "" } } }));
    try {
      const data = await api.getBorrowerProfile(borrowerId);
      set((s) => ({ profiles: { ...s.profiles, [borrowerId]: { data, loading: false, error: "" } } }));
    } catch (e) {
      set((s) => ({
        profiles: {
          ...s.profiles,
          [borrowerId]: { data: null, loading: false, error: e instanceof Error ? e.message : String(e) },
        },
      }));
    }
  },

  saveProfile: async (borrowerId, profile) => {
    // The endpoint returns the sealed document (updated_by / updated_at), so
    // the store takes its answer rather than the payload it just sent.
    const data = await api.updateBorrowerProfile(borrowerId, profile);
    set((s) => ({ profiles: { ...s.profiles, [borrowerId]: { data, loading: false, error: "" } } }));
  },

  financials: {},
  financialDocs: {},

  loadFinancials: async (borrowerId, force = false) => {
    const entry = get().financials[borrowerId];
    if (!force && entry && (entry.data || entry.loading)) return;
    set((s) => ({ financials: { ...s.financials, [borrowerId]: { data: null, loading: true, error: "" } } }));
    try {
      const data = await api.getBorrowerFinancials(borrowerId);
      set((s) => ({ financials: { ...s.financials, [borrowerId]: { data, loading: false, error: "" } } }));
    } catch (e) {
      set((s) => ({
        financials: {
          ...s.financials,
          [borrowerId]: { data: null, loading: false, error: e instanceof Error ? e.message : String(e) },
        },
      }));
    }
  },

  loadFinancialDocs: async (borrowerId, force = false) => {
    const entry = get().financialDocs[borrowerId];
    if (!force && entry && (entry.data || entry.loading)) return;
    set((s) => ({ financialDocs: { ...s.financialDocs, [borrowerId]: { data: null, loading: true, error: "" } } }));
    try {
      const { documents } = await api.getFinancialDocuments(borrowerId);
      set((s) => ({
        financialDocs: { ...s.financialDocs, [borrowerId]: { data: documents, loading: false, error: "" } },
      }));
    } catch (e) {
      // Expected for a non-admin: the endpoint is admin-only. The error is kept
      // on its own entry so the figures still render for them.
      set((s) => ({
        financialDocs: {
          ...s.financialDocs,
          [borrowerId]: { data: null, loading: false, error: e instanceof Error ? e.message : String(e) },
        },
      }));
    }
  },
}));
